/**
 * Rendering utilities: convert RoundedPolygon / Cubic[] to SVG paths and CSS clip-paths.
 *
 * All output paths use absolute coordinates relative to the shape's natural
 * normalized space ([0,1]×[0,1]) or scaled to pixel dimensions.
 */

import type { RoundedPolygon } from "../core/rounded-polygon";
import type { Cubic } from "../math/cubic";
import type { Morph } from "../morph/morph";

// ─── toSvgPath ─────────────────────────────────────────────────────────────

/**
 * Converts a list of Cubic Bézier curves to an SVG path string.
 *
 * @param cubics - Array of Cubic curves (must be contiguous, closed)
 * @param width - Width in pixels to scale x coordinates (default: 1 = normalized)
 * @param height - Height in pixels to scale y coordinates (default: 1 = normalized)
 * @returns SVG path data string, e.g. "M 0.5 0 C 1 0 1 1 0.5 1 Z"
 *
 * @example
 * const path = toSvgPath(MD3Shapes.circle.cubics, 200, 200);
 * // Use in <path d={path} />
 */
export function toSvgPath(cubics: Cubic[], width = 1, height = 1): string {
	if (cubics.length === 0) return "";

	// Add 1.5% padding on all sides to prevent browser clip-path boundary cutoff
	const paddingX = width * 0.015;
	const paddingY = height * 0.015;
	const sx = width - 2 * paddingX;
	const sy = height - 2 * paddingY;

	const flipY = (y: number) => paddingY + sy * (1 - y);

	const parts: string[] = [
		`M ${fmt(paddingX + cubics[0].anchor0X * sx)} ${fmt(flipY(cubics[0].anchor0Y))}`,
	];

	for (const c of cubics) {
		parts.push(
			`C ${fmt(paddingX + c.control0X * sx)} ${fmt(flipY(c.control0Y))},${fmt(paddingX + c.control1X * sx)} ${fmt(flipY(c.control1Y))},${fmt(paddingX + c.anchor1X * sx)} ${fmt(flipY(c.anchor1Y))}`,
		);
	}

	parts.push("Z");
	return parts.join(" ");
}

// ─── toClipPath ────────────────────────────────────────────────────────────

/**
 * Converts a RoundedPolygon to a CSS `clip-path: path(...)` value.
 *
 * @param polygon - Normalized RoundedPolygon (values in [0,1])
 * @param width - Element width in pixels
 * @param height - Element height in pixels
 * @returns CSS clip-path value, e.g. "path('M ...')"
 *
 * @example
 * const clipPath = toClipPath(MD3Shapes.sunny, 200, 200);
 * element.style.clipPath = clipPath;
 */
export function toClipPath(
	polygon: RoundedPolygon,
	width: number,
	height: number,
): string {
	const pathData = toSvgPath(polygon.cubics, width, height);
	return `path('${pathData}')`;
}

// ─── interpolatePath ───────────────────────────────────────────────────────

/**
 * Returns a CSS clip-path for a morphed shape at the given progress.
 *
 * @param morph - A pre-constructed Morph instance
 * @param progress - Interpolation factor in [0, 1]
 * @param width - Element width in pixels
 * @param height - Element height in pixels
 * @returns CSS clip-path value
 *
 * @example
 * const morph = new Morph(MD3Shapes.circle, MD3Shapes.flower);
 * element.style.clipPath = interpolatePath(morph, 0.5, 200, 200);
 */
export function interpolatePath(
	morph: Morph,
	progress: number,
	width: number,
	height: number,
): string {
	const cubics = morph.asCubics(progress);
	return toClipPath({ cubics } as RoundedPolygon, width, height);
}

// ─── Internal formatting ───────────────────────────────────────────────────

/** Format a float to at most 4 decimal places. */
function fmt(v: number): string {
	// toFixed(4) then strip trailing zeros
	return Number(v.toFixed(4)).toString();
}
